import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Badge,
  Modal,
  Descriptions,
  theme,
  Form,
  Select,
  DatePicker,
  Input,
  message,
  Divider,
  Alert,
  Segmented,
} from "antd";
import {
  CalendarOutlined,
  ThunderboltOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  SplitCellsOutlined,
  ApartmentOutlined,
  ReloadOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

// Helper: Get timezone display name
const getTimezoneDisplayName = (tz, aemoRegion) => {
  const tzMap = {
    "Australia/Adelaide": "ACST/ACDT (Adelaide)",
    "Australia/Sydney": "AEST/AEDT (Sydney)",
    "Australia/Brisbane": "AEST (Brisbane)",
    "Australia/Melbourne": "AEST/AEDT (Melbourne)",
    "Australia/Perth": "AWST (Perth)",
    "Australia/Darwin": "ACST (Darwin)",
    "Australia/Hobart": "AEST/AEDT (Hobart)",
  };
  if (tzMap[tz]) return tzMap[tz];
  // Fallback based on AEMO region
  const regionMap = {
    "SA1": "ACST/ACDT (SA)",
    "NSW1": "AEST/AEDT (NSW)",
    "VIC1": "AEST/AEDT (VIC)",
    "QLD1": "AEST (QLD)",
    "TAS1": "AEST/AEDT (TAS)",
  };
  return regionMap[aemoRegion] || tz || "Australia/Adelaide";
};

export default function BookedDispatch() {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  // Schedule modal states
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [selectedGroupTimezone, setSelectedGroupTimezone] = useState(null);

  // Edit modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [editGroupTimezone, setEditGroupTimezone] = useState(null);

  // Calendar view states
  const [calendarViewMode, setCalendarViewMode] = useState("7-day"); // "2-day" or "7-day"
  const [selectedDate, setSelectedDate] = useState(null); // For filtering table

  // Data states
  const [controlGroups, setControlGroups] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispatchesLoading, setDispatchesLoading] = useState(true);

  // Fetch all control groups (DUID only required for AEMO bidding, not for scheduling)
  const fetchControlGroups = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch("/api/control-groups", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setControlGroups(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch control groups:", error);
    }
  }, []);

  // Fetch strategies
  const fetchStrategies = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("token");
      const response = await fetch("/api/dispatch/strategies", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setStrategies(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch strategies:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch booked dispatches (all statuses for displaying upcoming and past)
  const fetchDispatches = useCallback(async () => {
    setDispatchesLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      // Fetch all dispatches (scheduled, completed, cancelled, etc.)
      const response = await fetch("/api/dispatch/list?pageSize=100", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await response.json();
      if (data.success) {
        setDispatches(data.data?.dispatches || []);
      }
    } catch (error) {
      console.error("Failed to fetch dispatches:", error);
    } finally {
      setDispatchesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchControlGroups();
    fetchStrategies();
    fetchDispatches();
  }, [fetchControlGroups, fetchStrategies, fetchDispatches]);

  // Get strategies by type
  const dispatchStrategies = strategies.filter(s => (s.type || s.strategy_type || "dispatch") === "dispatch");
  const allocationStrategies = strategies.filter(s => (s.type || s.strategy_type) === "allocation");

  // Handle control group selection change
  const handleControlGroupChange = (groupId) => {
    const group = controlGroups.find(g => g.id === groupId);
    if (group) {
      setSelectedGroupTimezone({
        timezone: group.timezone || 'Australia/Adelaide',
        aemoRegionId: group.aemoRegionId,
        displayName: getTimezoneDisplayName(group.timezone, group.aemoRegionId),
      });
    } else {
      setSelectedGroupTimezone(null);
    }
  };

  // Handle schedule dispatch
  const handleScheduleDispatch = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const values = form.getFieldsValue();
    setScheduling(true);

    try {
      const authToken = localStorage.getItem("token");
      const group = controlGroups.find(g => g.id === values.controlGroupId);
      const groupTimezone = group?.timezone || 'Australia/Adelaide';

      // Format times in group local timezone for sending to backend
      // The DatePicker returns dayjs objects in browser local time
      // We need to interpret them as group local time
      const startTimeStr = values.startTime.format("YYYY-MM-DD HH:mm:ss");
      const endTimeStr = values.endTime.format("YYYY-MM-DD HH:mm:ss");

      const payload = {
        controlGroupId: values.controlGroupId,
        groupLocalTimezone: groupTimezone,
        // Send the time string as-is, backend will interpret it as group local time
        scheduledStartLocal: startTimeStr,
        scheduledEndLocal: endTimeStr,
        dispatchStrategyId: values.dispatchStrategyId,
        allocationStrategyId: values.allocationStrategyId || null,
        notes: values.notes || null,
      };

      const response = await fetch("/api/dispatch/schedule", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        message.success("Dispatch scheduled successfully");
        setScheduleModalOpen(false);
        form.resetFields();
        setSelectedGroupTimezone(null);
        fetchDispatches(); // Refresh dispatches list
      } else {
        message.error(data.message || "Failed to schedule dispatch");
      }
    } catch (error) {
      console.error("Error scheduling dispatch:", error);
      message.error("Failed to schedule dispatch");
    } finally {
      setScheduling(false);
    }
  };

  // Open schedule modal
  const openScheduleModal = () => {
    form.resetFields();
    setSelectedGroupTimezone(null);
    form.setFieldsValue({
      startTime: dayjs().add(1, "hour").startOf("hour"),
      endTime: dayjs().add(2, "hour").startOf("hour"),
    });
    setScheduleModalOpen(true);
  };

  // Open edit modal with existing dispatch data
  const openEditModal = (dispatch) => {
    setSelectedDispatch(dispatch);
    const group = controlGroups.find(g => g.id === dispatch.controlGroupId);
    const tz = dispatch.groupLocalTimezone || group?.timezone || 'Australia/Adelaide';

    setEditGroupTimezone({
      timezone: tz,
      aemoRegionId: group?.aemoRegionId,
      displayName: getTimezoneDisplayName(tz, group?.aemoRegionId),
    });

    // Pre-populate form with dispatch data
    // Use scheduledStartLocal if available, otherwise use scheduledStart
    const startTime = dispatch.scheduledStartLocal || dispatch.scheduledStart;
    const endTime = dispatch.scheduledEndLocal || dispatch.scheduledEnd;

    editForm.setFieldsValue({
      controlGroupId: dispatch.controlGroupId,
      startTime: startTime ? dayjs(startTime) : null,
      endTime: endTime ? dayjs(endTime) : null,
      dispatchStrategyId: dispatch.strategyId || null,
      allocationStrategyId: dispatch.allocationStrategyId || null,
      notes: dispatch.notes || "",
    });
    setEditModalOpen(true);
  };

  // Handle edit control group change
  const handleEditControlGroupChange = (groupId) => {
    const group = controlGroups.find(g => g.id === groupId);
    if (group) {
      setEditGroupTimezone({
        timezone: group.timezone || 'Australia/Adelaide',
        aemoRegionId: group.aemoRegionId,
        displayName: getTimezoneDisplayName(group.timezone, group.aemoRegionId),
      });
    } else {
      setEditGroupTimezone(null);
    }
  };

  // Handle edit dispatch
  const handleEditDispatch = async () => {
    try {
      await editForm.validateFields();
    } catch {
      return;
    }

    const values = editForm.getFieldsValue();
    setEditing(true);

    try {
      const authToken = localStorage.getItem("token");
      const group = controlGroups.find(g => g.id === values.controlGroupId);
      const groupTimezone = group?.timezone || 'Australia/Adelaide';

      const startTimeStr = values.startTime.format("YYYY-MM-DD HH:mm:ss");
      const endTimeStr = values.endTime.format("YYYY-MM-DD HH:mm:ss");

      const payload = {
        control_group_id: values.controlGroupId,
        group_local_timezone: groupTimezone,
        scheduled_start: startTimeStr,
        scheduled_end: endTimeStr,
        dispatch_strategy_id: values.dispatchStrategyId || null,
        allocation_strategy_id: values.allocationStrategyId || null,
        notes: values.notes || null,
      };

      const response = await fetch(`/api/dispatch/${selectedDispatch.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        message.success("Dispatch updated successfully");
        setEditModalOpen(false);
        editForm.resetFields();
        setEditGroupTimezone(null);
        setDetailModalOpen(false);
        fetchDispatches();
      } else {
        message.error(data.message || "Failed to update dispatch");
      }
    } catch (error) {
      console.error("Error updating dispatch:", error);
      message.error("Failed to update dispatch");
    } finally {
      setEditing(false);
    }
  };

  // Handle delete dispatch
  const handleDeleteDispatch = (dispatch) => {
    Modal.confirm({
      title: "Cancel Dispatch",
      content: (
        <div>
          <p>Are you sure you want to cancel this dispatch?</p>
          <div style={{ marginTop: 8, padding: 8, background: "#fafafa", borderRadius: 4 }}>
            <div><strong>ID:</strong> {dispatch.id}</div>
            <div><strong>Control Group:</strong> {dispatch.controlGroupName}</div>
            <div><strong>Scheduled:</strong> {dayjs(dispatch.scheduledStart).format("YYYY-MM-DD HH:mm")}</div>
          </div>
        </div>
      ),
      okText: "Cancel Dispatch",
      okType: "danger",
      cancelText: "Keep",
      onOk: async () => {
        try {
          const authToken = localStorage.getItem("token");
          const response = await fetch(`/api/dispatch/${dispatch.id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${authToken}` },
          });
          const data = await response.json();
          if (data.success) {
            message.success("Dispatch cancelled successfully");
            setDetailModalOpen(false);
            fetchDispatches();
          } else {
            message.error(data.message || "Failed to cancel dispatch");
          }
        } catch (error) {
          console.error("Error cancelling dispatch:", error);
          message.error("Failed to cancel dispatch");
        }
      },
    });
  };

  // Split dispatches into upcoming and past
  // Upcoming = scheduledStart > now (hasn't started yet)
  // Past = scheduledStart <= now (already started, whether running or completed)
  const now = dayjs();
  const upcomingDispatches = dispatches.filter((d) => {
    const scheduledStart = d.scheduledStart ? dayjs(d.scheduledStart) : null;
    const actualStart = d.actualStart ? dayjs(d.actualStart) : null;
    // If actualStart exists and is before now, it has already started
    if (actualStart && actualStart.isBefore(now)) return false;
    // If scheduledStart exists and is before now, it should have started
    if (scheduledStart && scheduledStart.isBefore(now)) return false;
    // Otherwise it's upcoming (hasn't started yet)
    return true;
  });

  const pastDispatches = dispatches.filter((d) => {
    const scheduledStart = d.scheduledStart ? dayjs(d.scheduledStart) : null;
    const actualStart = d.actualStart ? dayjs(d.actualStart) : null;
    // If actualStart exists and is before now, it has started (past or running)
    if (actualStart && actualStart.isBefore(now)) return true;
    // If scheduledStart exists and is before now, it should have started
    if (scheduledStart && scheduledStart.isBefore(now)) return true;
    return false;
  });

  // Stats based on upcoming dispatches
  const stats = {
    today: upcomingDispatches.filter((d) => dayjs(d.scheduledStart).isSame(dayjs(), "day")).length,
    thisWeek: upcomingDispatches.filter((d) => dayjs(d.scheduledStart).isSame(dayjs(), "week")).length,
    total: upcomingDispatches.length,
    past: pastDispatches.length,
  };

  // Get calendar days based on view mode
  const getCalendarDays = () => {
    const days = [];
    const daysCount = calendarViewMode === "2-day" ? 2 : 7;
    for (let i = 0; i < daysCount; i++) {
      days.push(dayjs().add(i, "day"));
    }
    return days;
  };

  const calendarDays = getCalendarDays();

  // Get dispatches for a specific day (upcoming only)
  const getDispatchesForDay = (day) => {
    return upcomingDispatches.filter((d) =>
      dayjs(d.scheduledStart).format("YYYY-MM-DD") === day.format("YYYY-MM-DD")
    );
  };

  // Handle day click for filtering
  const handleDayClick = (day) => {
    const dateStr = day.format("YYYY-MM-DD");
    if (selectedDate === dateStr) {
      setSelectedDate(null); // Deselect
    } else {
      setSelectedDate(dateStr); // Select
    }
  };

  // Filtered dispatches for table (upcoming only)
  const filteredDispatches = selectedDate
    ? upcomingDispatches.filter((d) => dayjs(d.scheduledStart).format("YYYY-MM-DD") === selectedDate)
    : upcomingDispatches;

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "14%",
      align: "center",
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "dispatchType",
      key: "dispatchType",
      width: "10%",
      align: "center",
      render: (type) => {
        const colors = {
          "FCAS_RAISE": "blue",
          "FCAS_LOWER": "green",
          "PEAK_SHAVING": "orange",
          "ARBITRAGE": "purple",
          "MANUAL": "default",
        };
        return <Tag color={colors[type] || "default"}>{type || "MANUAL"}</Tag>;
      },
    },
    {
      title: "Control Group",
      dataIndex: "controlGroupName",
      key: "controlGroupName",
      width: "18%",
      align: "center",
      ellipsis: true,
    },
    {
      title: "Scheduled",
      dataIndex: "scheduledStart",
      key: "scheduledStart",
      width: "16%",
      align: "center",
      render: (time) => dayjs(time).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "Power",
      dataIndex: "targetPowerKw",
      key: "targetPowerKw",
      width: "10%",
      align: "center",
      render: (v) => v ? `${Number(v).toLocaleString()} kW` : "-",
    },
    {
      title: "Devices",
      dataIndex: "totalDevices",
      key: "totalDevices",
      width: "8%",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "10%",
      align: "center",
      render: (status) => (
        <Tag color={status === "scheduled" ? "success" : "warning"}>
          {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "14%",
      align: "center",
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedDispatch(record);
              setDetailModalOpen(true);
            }}
            style={{ padding: 0 }}
          >
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0 }}
            onClick={() => openEditModal(record)}
          />
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            style={{ padding: 0 }}
            onClick={() => handleDeleteDispatch(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <PageLayout active="dispatchBooked">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Booked Dispatches
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Scheduled future dispatch operations
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchDispatches} loading={dispatchesLoading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} size="large" onClick={openScheduleModal}>
              Schedule Dispatch
            </Button>
          </Space>
        </div>
      </div>

      {/* Row 1: Stats Cards - 4 in a row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Today"
              value={stats.today}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: token.colorPrimary }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="This Week"
              value={stats.thisWeek}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: "#f5a623" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Scheduled"
              value={stats.total}
              valueStyle={{ color: token.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Started"
              value={stats.past}
              prefix={<HistoryOutlined />}
              valueStyle={{ color: "#8c8c8c" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Row 2: Calendar View */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 16 }}
        title={<Text strong>Calendar View</Text>}
        extra={
          <Segmented
            value={calendarViewMode}
            onChange={setCalendarViewMode}
            options={[
              { label: "2 Days", value: "2-day" },
              { label: "7 Days", value: "7-day" },
            ]}
          />
        }
      >
        <div style={{ display: "flex", gap: 12, overflowX: "auto" }}>
          {calendarDays.map((day) => {
            const dayDispatches = getDispatchesForDay(day);
            const isToday = day.isSame(dayjs(), "day");
            const isSelected = selectedDate === day.format("YYYY-MM-DD");

            return (
              <div
                key={day.format("YYYY-MM-DD")}
                onClick={() => handleDayClick(day)}
                style={{
                  flex: calendarViewMode === "2-day" ? 1 : "0 0 calc((100% - 72px) / 7)",
                  minWidth: calendarViewMode === "2-day" ? 200 : 120,
                  padding: 12,
                  borderRadius: 8,
                  border: isSelected
                    ? `2px solid ${token.colorPrimary}`
                    : `1px solid ${token.colorBorderSecondary}`,
                  background: isSelected
                    ? token.colorPrimaryBg
                    : isToday
                    ? "rgba(245, 166, 35, 0.08)"
                    : token.colorBgContainer,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {/* Day Header */}
                <div style={{ marginBottom: 8, textAlign: "center" }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block" }}
                  >
                    {day.format("ddd")}
                  </Text>
                  <Text
                    strong
                    style={{
                      fontSize: 20,
                      color: isToday ? "#f5a623" : token.colorText,
                    }}
                  >
                    {day.format("D")}
                  </Text>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block" }}
                  >
                    {day.format("MMM")}
                  </Text>
                </div>

                {/* Dispatch Summary */}
                <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 8 }}>
                  {dayDispatches.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      No dispatches
                    </Text>
                  ) : (
                    <>
                      <div style={{ marginBottom: 4 }}>
                        <Badge
                          status="success"
                          text={
                            <Text style={{ fontSize: 12 }}>
                              {dayDispatches.length} dispatch{dayDispatches.length > 1 ? "es" : ""}
                            </Text>
                          }
                        />
                      </div>
                      {dayDispatches.slice(0, 2).map((d) => (
                        <div key={d.id} style={{ marginBottom: 2 }}>
                          <Tag
                            color="blue"
                            style={{ fontSize: 10, padding: "0 4px", margin: 0 }}
                          >
                            {dayjs(d.scheduledStart).format("HH:mm")}
                          </Tag>
                          <Text style={{ fontSize: 10, marginLeft: 4 }}>
                            {d.controlGroupName?.substring(0, 8) || "—"}
                          </Text>
                        </div>
                      ))}
                      {dayDispatches.length > 2 && (
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          +{dayDispatches.length - 2} more
                        </Text>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {selectedDate && (
          <div style={{ marginTop: 12, textAlign: "center" }}>
            <Tag
              closable
              onClose={() => setSelectedDate(null)}
              color="blue"
            >
              Filtering: {dayjs(selectedDate).format("MMM D, YYYY")}
            </Tag>
          </div>
        )}
      </Card>

      {/* Row 3: Table View - Full Width */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <Text strong>Upcoming Dispatches</Text>
            {selectedDate && (
              <Tag color="blue">
                {dayjs(selectedDate).format("MMM D, YYYY")}
              </Tag>
            )}
          </Space>
        }
        extra={
          selectedDate && (
            <Button size="small" onClick={() => setSelectedDate(null)}>
              Show All
            </Button>
          )
        }
      >
        <Table
          columns={columns}
          dataSource={filteredDispatches}
          rowKey="id"
          pagination={makePagination(pageSize, setPageSize)}
          size="middle"
          loading={dispatchesLoading}
          locale={{
            emptyText: selectedDate
              ? `No dispatches scheduled for ${dayjs(selectedDate).format("MMM D, YYYY")}`
              : "No upcoming dispatches",
          }}
        />
      </Card>

      {/* Row 4: Started & Past Dispatches */}
      {pastDispatches.length > 0 && (
        <Card
          bordered={false}
          style={{ borderRadius: 12, marginTop: 16 }}
          title={
            <Space>
              <HistoryOutlined style={{ color: "#8c8c8c" }} />
              <Text strong style={{ color: "#8c8c8c" }}>Started & Past Dispatches</Text>
              <Tag color="default">{pastDispatches.length}</Tag>
            </Space>
          }
        >
          <Table
            columns={[
              ...columns.slice(0, -2), // All columns except Status and Actions
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                width: "10%",
                align: "center",
                render: (status) => {
                  const statusColors = {
                    completed: "success",
                    cancelled: "error",
                    error: "error",
                    running: "processing",
                    scheduled: "warning",
                  };
                  return (
                    <Tag color={statusColors[status] || "default"}>
                      {status?.charAt(0).toUpperCase() + status?.slice(1)}
                    </Tag>
                  );
                },
              },
              {
                title: "Actions",
                key: "actions",
                width: "10%",
                align: "center",
                render: (_, record) => (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      setSelectedDispatch(record);
                      setDetailModalOpen(true);
                    }}
                    style={{ padding: 0 }}
                  >
                    View
                  </Button>
                ),
              },
            ]}
            dataSource={pastDispatches}
            rowKey="id"
            pagination={{ pageSize: 5 }}
            size="small"
            loading={dispatchesLoading}
            locale={{ emptyText: "No past dispatches" }}
          />
        </Card>
      )}

      {/* Detail Modal */}
      <Modal
        title={selectedDispatch?.id}
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        footer={[
          <Button
            key="delete"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteDispatch(selectedDispatch)}
          >
            Cancel Dispatch
          </Button>,
          <Button key="close" onClick={() => setDetailModalOpen(false)}>
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => openEditModal(selectedDispatch)}
          >
            Edit Dispatch
          </Button>,
        ]}
      >
        {selectedDispatch && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Type">
              <Tag color="blue">{selectedDispatch.dispatchType || "MANUAL"}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Control Group">
              {selectedDispatch.controlGroupName}
            </Descriptions.Item>
            <Descriptions.Item label="Scheduled Start">
              {dayjs(selectedDispatch.scheduledStart).format("YYYY-MM-DD HH:mm")}
            </Descriptions.Item>
            <Descriptions.Item label="Scheduled End">
              {selectedDispatch.scheduledEnd ? dayjs(selectedDispatch.scheduledEnd).format("YYYY-MM-DD HH:mm") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Target Power">
              {selectedDispatch.targetPowerKw ? `${Number(selectedDispatch.targetPowerKw).toLocaleString()} kW` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Total Devices">
              {selectedDispatch.totalDevices || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Strategy">
              {selectedDispatch.strategyName || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedDispatch.status === "scheduled" ? "success" : "warning"}>
                {selectedDispatch.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Scheduled By">
              {selectedDispatch.scheduledByName || "-"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Schedule Dispatch Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            Schedule New Dispatch
          </Space>
        }
        open={scheduleModalOpen}
        onCancel={() => setScheduleModalOpen(false)}
        onOk={handleScheduleDispatch}
        okText="Schedule"
        confirmLoading={scheduling}
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {/* Control Group Selection */}
          <Form.Item
            name="controlGroupId"
            label={
              <Space>
                <ApartmentOutlined />
                Control Group
              </Space>
            }
            rules={[{ required: true, message: "Please select a control group" }]}
          >
            <Select
              placeholder="Select control group"
              showSearch
              optionFilterProp="label"
              loading={loading}
              onChange={handleControlGroupChange}
              options={controlGroups.map(g => ({
                value: g.id,
                label: g.name + (g.duid ? ` (${g.duid})` : ""),
              }))}
              optionRender={(option) => {
                const group = controlGroups.find(g => g.id === option.value);
                return (
                  <Space>
                    <span>{group?.name}</span>
                    {group?.duid && <Tag color="blue">{group.duid}</Tag>}
                    {group?.timezone && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {getTimezoneDisplayName(group.timezone, group.aemoRegionId)}
                      </Text>
                    )}
                  </Space>
                );
              }}
            />
          </Form.Item>

          {/* Timezone Alert */}
          {selectedGroupTimezone && (
            <Alert
              type="info"
              showIcon
              icon={<ClockCircleOutlined />}
              message={
                <span>
                  Times are in <strong>{selectedGroupTimezone.displayName}</strong> timezone
                </span>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Time Selection */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label={
                  <Space>
                    <span>Start Time</span>
                    {selectedGroupTimezone && (
                      <Tag color="blue" style={{ fontSize: 10 }}>
                        {selectedGroupTimezone.displayName}
                      </Tag>
                    )}
                  </Space>
                }
                rules={[{ required: true, message: "Start time is required" }]}
              >
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label={
                  <Space>
                    <span>End Time</span>
                    {selectedGroupTimezone && (
                      <Tag color="blue" style={{ fontSize: 10 }}>
                        {selectedGroupTimezone.displayName}
                      </Tag>
                    )}
                  </Space>
                }
                rules={[{ required: true, message: "End time is required" }]}
              >
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" orientationMargin={0}>
            <Text type="secondary">Strategy Selection</Text>
          </Divider>

          {/* Dispatch Strategy */}
          <Form.Item
            name="dispatchStrategyId"
            label={
              <Space>
                <ThunderboltOutlined style={{ color: token.colorPrimary }} />
                Dispatch Strategy
              </Space>
            }
            rules={[{ required: true, message: "Please select a dispatch strategy" }]}
            extra="Determines when and how the group should dispatch"
          >
            <Select
              placeholder="Select dispatch strategy"
              loading={loading}
              options={dispatchStrategies.map(s => ({
                value: s.id,
                label: (
                  <div>
                    <div>{s.name}</div>
                    {s.description && (
                      <Text type="secondary" style={{ fontSize: 11 }}>{s.description}</Text>
                    )}
                  </div>
                ),
              }))}
              notFoundContent={
                dispatchStrategies.length === 0 ? (
                  <Alert
                    type="warning"
                    message="No dispatch strategies found"
                    description="Create a dispatch strategy in Strategy Centre first"
                    showIcon
                  />
                ) : null
              }
            />
          </Form.Item>

          {/* Allocation Strategy */}
          <Form.Item
            name="allocationStrategyId"
            label={
              <Space>
                <SplitCellsOutlined style={{ color: "#f5a623" }} />
                Allocation Strategy
              </Space>
            }
            extra="Determines how group commands are distributed to individual systems (optional - uses default if not selected)"
          >
            <Select
              placeholder="Select allocation strategy (optional)"
              loading={loading}
              allowClear
              options={allocationStrategies.map(s => ({
                value: s.id,
                label: (
                  <div>
                    <div>{s.name}</div>
                    {s.description && (
                      <Text type="secondary" style={{ fontSize: 11 }}>{s.description}</Text>
                    )}
                  </div>
                ),
              }))}
              notFoundContent={
                allocationStrategies.length === 0 ? (
                  <Text type="secondary">No allocation strategies defined - will use default distribution</Text>
                ) : null
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Dispatch Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            Edit Dispatch
          </Space>
        }
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          editForm.resetFields();
          setEditGroupTimezone(null);
        }}
        onOk={handleEditDispatch}
        okText="Save Changes"
        confirmLoading={editing}
        width={600}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          {/* Dispatch ID (read-only) */}
          {selectedDispatch && (
            <div style={{ marginBottom: 16, padding: 8, background: "#fafafa", borderRadius: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Dispatch ID: </Text>
              <Text strong>{selectedDispatch.id}</Text>
            </div>
          )}

          {/* Control Group Selection */}
          <Form.Item
            name="controlGroupId"
            label={
              <Space>
                <ApartmentOutlined />
                Control Group
              </Space>
            }
            rules={[{ required: true, message: "Please select a control group" }]}
          >
            <Select
              placeholder="Select control group"
              showSearch
              optionFilterProp="label"
              loading={loading}
              onChange={handleEditControlGroupChange}
              options={controlGroups.map(g => ({
                value: g.id,
                label: g.name + (g.duid ? ` (${g.duid})` : ""),
              }))}
              optionRender={(option) => {
                const group = controlGroups.find(g => g.id === option.value);
                return (
                  <Space>
                    <span>{group?.name}</span>
                    {group?.duid && <Tag color="blue">{group.duid}</Tag>}
                    {group?.timezone && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {getTimezoneDisplayName(group.timezone, group.aemoRegionId)}
                      </Text>
                    )}
                  </Space>
                );
              }}
            />
          </Form.Item>

          {/* Timezone Alert */}
          {editGroupTimezone && (
            <Alert
              type="info"
              showIcon
              icon={<ClockCircleOutlined />}
              message={
                <span>
                  Times are in <strong>{editGroupTimezone.displayName}</strong> timezone
                </span>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Time Selection */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label={
                  <Space>
                    <span>Start Time</span>
                    {editGroupTimezone && (
                      <Tag color="blue" style={{ fontSize: 10 }}>
                        {editGroupTimezone.displayName}
                      </Tag>
                    )}
                  </Space>
                }
                rules={[{ required: true, message: "Start time is required" }]}
              >
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endTime"
                label={
                  <Space>
                    <span>End Time</span>
                    {editGroupTimezone && (
                      <Tag color="blue" style={{ fontSize: 10 }}>
                        {editGroupTimezone.displayName}
                      </Tag>
                    )}
                  </Space>
                }
                rules={[{ required: true, message: "End time is required" }]}
              >
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" orientationMargin={0}>
            <Text type="secondary">Strategy Selection</Text>
          </Divider>

          {/* Dispatch Strategy */}
          <Form.Item
            name="dispatchStrategyId"
            label={
              <Space>
                <ThunderboltOutlined style={{ color: token.colorPrimary }} />
                Dispatch Strategy
              </Space>
            }
            extra="Determines when and how the group should dispatch"
          >
            <Select
              placeholder="Select dispatch strategy"
              loading={loading}
              allowClear
              options={dispatchStrategies.map(s => ({
                value: s.id,
                label: (
                  <div>
                    <div>{s.name}</div>
                    {s.description && (
                      <Text type="secondary" style={{ fontSize: 11 }}>{s.description}</Text>
                    )}
                  </div>
                ),
              }))}
            />
          </Form.Item>

          {/* Allocation Strategy */}
          <Form.Item
            name="allocationStrategyId"
            label={
              <Space>
                <SplitCellsOutlined style={{ color: "#f5a623" }} />
                Allocation Strategy
              </Space>
            }
            extra="Determines how group commands are distributed to individual systems"
          >
            <Select
              placeholder="Select allocation strategy (optional)"
              loading={loading}
              allowClear
              options={allocationStrategies.map(s => ({
                value: s.id,
                label: (
                  <div>
                    <div>{s.name}</div>
                    {s.description && (
                      <Text type="secondary" style={{ fontSize: 11 }}>{s.description}</Text>
                    )}
                  </div>
                ),
              }))}
            />
          </Form.Item>

          {/* Notes */}
          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea
              placeholder="Add notes (optional)"
              rows={2}
            />
          </Form.Item>
        </Form>
      </Modal>
    </PageLayout>
  );
}
